import {
	faClockRotateLeft,
	faDiamondExclamation,
	faPrescription,
	faScaleUnbalanced,
	faSnowflake,
	faStopwatch,
	faWeightHanging,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ItemDesignationsDto } from '@supply-mover-2-0/shared';
import { Tooltip, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

interface ItemDesignationBadgesProps {
	designations: ItemDesignationsDto;
}

/**
 * Compact inline badges for item regulatory and handling designations.
 * Matches the CRM's ItemAttributeIcons pattern but adapted for the portal.
 * Returns null when no designations apply so no extra space is consumed.
 */
export function ItemDesignationBadges({
	designations,
}: ItemDesignationBadgesProps): JSX.Element | null {
	const { t } = useTranslation();

	// DB may contain mixed-case schedules (e.g. 'Rx') from legacy imports; DTO contract is uppercase.
	const deaSchedule = designations.deaSchedule?.toUpperCase() ?? null;
	const isRx = deaSchedule === 'RX';
	const isControlled = deaSchedule != null && deaSchedule.startsWith('C');

	const controlledLabel =
		isControlled && deaSchedule != null
			? t(`Controlled Substance - ${deaSchedule}`)
			: t('Controlled Substance');

	// Return null if no designations apply — avoids wasted space
	if (
		!isRx &&
		!isControlled &&
		!designations.isShortDate &&
		!designations.isRefrigerated &&
		!designations.isHazmat &&
		!designations.isHeavy &&
		!designations.isPreviouslyPurchased
	) {
		return null;
	}

	return (
		<div className="flex items-center gap-1.5">
			{isRx && (
				<Tooltip content={t('Prescription')} position="top">
					<span className="text-blue-500" aria-label={t('Prescription')}>
						<FontAwesomeIcon icon={faPrescription} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{isControlled && (
				<Tooltip content={controlledLabel} position="top">
					<span className="text-purple-500" aria-label={controlledLabel}>
						<FontAwesomeIcon icon={faScaleUnbalanced} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{designations.isShortDate && (
				<Tooltip content={t('Short Date')} position="top">
					<span className="text-red-500" aria-label={t('Short Date')}>
						<FontAwesomeIcon icon={faStopwatch} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{designations.isRefrigerated && (
				<Tooltip content={t('Refrigerated')} position="top">
					<span className="text-blue-500" aria-label={t('Refrigerated')}>
						<FontAwesomeIcon icon={faSnowflake} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{designations.isHazmat && (
				<Tooltip content={t('Hazardous Material')} position="top">
					<span className="text-red-500" aria-label={t('Hazardous Material')}>
						<FontAwesomeIcon icon={faDiamondExclamation} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{designations.isHeavy && (
				<Tooltip content={t('Heavy Item')} position="top">
					<span className="text-amber-500" aria-label={t('Heavy Item')}>
						<FontAwesomeIcon icon={faWeightHanging} className="text-xs" />
					</span>
				</Tooltip>
			)}
			{designations.isPreviouslyPurchased && (
				<Tooltip content={t('Previously Purchased')} position="top">
					<span
						className="text-green-500"
						aria-label={t('Previously Purchased')}
					>
						<FontAwesomeIcon icon={faClockRotateLeft} className="text-xs" />
					</span>
				</Tooltip>
			)}
		</div>
	);
}
